import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Button, Chip, Dropdown, DropdownOption, Icon } from '@column/column-ui-kit';

import { PopoverAction, PopoverBase, PopoverBaseProps } from '../Base';
import { TransferStatus, formatTransferStatus } from '~/lib/transfers';
import { TransferStatusObject } from '~/repositories/Transfer/TransferRepository';
import { FormElement, FormLabel } from '~/styles';
import { formatString } from '~/util';

export type PopoverFilterTransferStatusData = string;

interface PopoverFilterTransferStatusProps extends PopoverBaseProps {
  data: PopoverFilterTransferStatusData[];
  onSubmit?: (data: PopoverFilterTransferStatusData[]) => void;
}

const Grid = styled.div`
  display: grid;
  grid-gap: 16px;
  padding: 12px 12px 0 12px;
`;

const Wrapper = styled(PopoverBase)`
  --popover-padding: 0px;
  --popover-width: 248px;
  --popover-left: 0px;
`;

const List = styled.div`
  display: grid;
  grid-gap: 8px;
`;

const Entry = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.background};
`;

export const PopoverFilterTransferStatus: React.FC<PopoverFilterTransferStatusProps> = ({
  data,
  onSubmit,
  ...popoverProps
}) => {
  const [currentData, setCurrentData] = useState<PopoverFilterTransferStatusData[]>(data ?? []);

  const handleClick = useCallback(
    (entry: string) => {
      if (currentData.includes(entry)) {
        setCurrentData((s) => s.filter((k) => k !== entry));
        return;
      }
      setCurrentData((s) => [...s, entry]);
    },
    [currentData]
  );

  const handleRemove = useCallback(
    (entry: string) => {
      if (!currentData.includes(entry)) {
        return;
      }
      setCurrentData((s) => s.filter((k) => k !== entry));
    },
    [currentData]
  );

  const entries = useMemo(() => {
    const array: DropdownOption[] = [];

    for (const key in TransferStatusObject) {
      if (TransferStatusObject.hasOwnProperty(key)) {
        array.push({
          label: formatString(key).toUpperCase(),
          isDivider: true,
        });

        const values = TransferStatusObject[key as keyof typeof TransferStatusObject];

        values.forEach((entry) => {
          array.push({
            label: formatTransferStatus(entry as TransferStatus),
            value: entry,
            onClick: () => handleClick(entry),
          });
        });
      }
    }

    return array;
  }, []);

  const handleResetClick = () => {
    setCurrentData([]);

    if (onSubmit) {
      onSubmit([]);
    }
  };

  const handleApplyClick = () => {
    if (onSubmit) {
      onSubmit(currentData);
    }
  };

  useEffect(() => {
    setCurrentData(data);
  }, [data, popoverProps.show]);

  return (
    <Wrapper {...popoverProps}>
      <Grid>
        <FormElement>
          <FormLabel>Status</FormLabel>
          <List>
            {[...new Set(currentData)].map((entry) => (
              <Entry key={entry}>
                <Chip>{formatTransferStatus(entry as TransferStatus)}</Chip>
                <Button size="tiny" variant="subtle" icon={<Icon.Cross />} onClick={() => handleRemove(entry)} />
              </Entry>
            ))}
            <Dropdown
              search
              active={currentData}
              options={entries}
              size="small"
              label="Add status..."
              renderInside
              fullWidth
            />
          </List>
        </FormElement>
      </Grid>
      <PopoverAction>
        <Button size="small" variant="secondary" onClick={handleResetClick}>
          Reset
        </Button>
        <Button size="small" onClick={handleApplyClick}>
          Apply
        </Button>
      </PopoverAction>
    </Wrapper>
  );
};
